import React, { useState, useEffect, useCallback, useRef } from "react";

const defaultPhotos = Array.from({ length: 5 }, (_, i) => ({
  src: "",
  alt: `Friendship Fiesta moment ${String(i + 1).padStart(2, "0")}`,
}));

function PhotoIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4a4a4a" strokeWidth="1.4">
      <rect x="3" y="6" width="18" height="14" rx="1.5" />
      <circle cx="12" cy="13" r="3.4" />
      <path d="M8 6l1.2-2h5.6L16 6" />
    </svg>
  );
}

function Frame({ photo, onOpen, num }) {
  const clickable = photo && photo.src;
  return (
    <div
      className={clickable ? "ff-frame clickable" : "ff-frame"}
      onClick={clickable ? onOpen : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onOpen();
            }
          : undefined
      }
      aria-label={clickable ? `Open ${photo.alt || `photo ${num}`} full screen` : undefined}
    >
      {clickable ? (
        <img src={photo.src} alt={photo.alt || `Friendship Fiesta moment ${num}`} draggable="false" />
      ) : (
        <div className="ff-placeholder">
          <PhotoIcon />
          Add photo
        </div>
      )}
    </div>
  );
}

function Lightbox({ photos, activeIndex, onClose, onPrev, onNext }) {
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onPrev, onNext]);

  if (activeIndex === null) return null;
  const photo = photos[activeIndex];
  const num = String(activeIndex + 1).padStart(2, "0");

  return (
    <div className="ff-lightbox-overlay" onClick={onClose}>
      <button className="ff-lightbox-close" onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <button
        className="ff-lightbox-nav ff-lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Previous photo"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      <div className="ff-lightbox-stage" onClick={(e) => e.stopPropagation()}>
        <img src={photo.src} alt={photo.alt || `Friendship Fiesta moment ${num}`} />
        <div className="ff-lightbox-caption">
          <span className="ff-lightbox-num">{num}</span>
          <span>{photo.alt || `Friendship Fiesta moment ${num}`}</span>
          <span className="ff-lightbox-count">
            {num} / {String(photos.length).padStart(2, "0")}
          </span>
        </div>
      </div>

      <button
        className="ff-lightbox-nav ff-lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Next photo"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

const FRAME_WIDTH = 440; // px, wide (landscape) frames so photos aren't cropped tall — keep in sync with --ff-frame-w
const FRAME_GAP = 16; // px, gap between photos — keep in sync with --ff-frame-gap

export default function TedxFriendshipfiesta({ photos = defaultPhotos }) {
  const trackPhotos = [...photos, ...photos];
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeDot, setActiveDot] = useState(0);
  const dotCount = Math.min(6, Math.max(photos.length, 1));

  const openAt = useCallback((i) => setActiveIndex(i), []);
  const close = useCallback(() => setActiveIndex(null), []);
  const goPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  );
  const goNext = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % photos.length)),
    [photos.length]
  );

  // Cosmetic dot progress synced loosely to the marquee animation duration
  useEffect(() => {
    const totalMs = 48000;
    const step = totalMs / dotCount;
    const id = setInterval(() => {
      setActiveDot((d) => (d + 1) % dotCount);
    }, step);
    return () => clearInterval(id);
  }, [dotCount]);

  useEffect(() => {
    document.body.style.overflow = activeIndex !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeIndex]);

  // Red particle field (ported from theme.jsx), scoped to this section's own height
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = sectionRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId;
    const particles = [];
    const particleCount = 220;

    const getSize = () => {
      const rect = container.getBoundingClientRect();
      return { w: rect.width, h: rect.height };
    };

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const { w, h } = getSize();
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    class Particle {
      constructor() {
        this.reset();
        this.y = Math.random() * getSize().h;
      }
      reset() {
        const { w } = getSize();
        this.x = Math.random() * w;
        this.y = -10;
        this.speed = Math.random() * 0.3 + 0.1;
        this.size = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
      }
      update() {
        const { h } = getSize();
        this.y += this.speed;
        if (this.y > h) this.reset();
      }
      draw() {
        ctx.fillStyle = `rgba(226, 43, 30, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    resizeCanvas();
    for (let i = 0; i < particleCount; i++) particles.push(new Particle());

    const animate = () => {
      const { w, h } = getSize();
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.update();
        p.draw();
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    window.addEventListener("resize", resizeCanvas);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <section className="ff-section" ref={sectionRef}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');

        .ff-section{
          --red:#E1001A;
          --black:#000000;
          --white:#FFFFFF;
          --off:#D9D9D9;
          --grey:#8A8A8A;
          --line:#2A2A2A;
          --ff-frame-w: ${FRAME_WIDTH}px;
          --ff-frame-gap: ${FRAME_GAP}px;
          background:var(--black);
          color:var(--white);
          font-family:'Poppins',sans-serif;
          padding:56px 0 44px;
          position:relative;
          overflow:hidden;
        }
        .ff-section *{box-sizing:border-box;}

        .ff-canvas{
          position:absolute;
          top:0;
          left:0;
          inset:0;
          width:100%;
          height:calc(100% + 120px);
          pointer-events:none;
          z-index:0;
        }

        .ff-header, .ff-strip-row, .ff-footer{
          position:relative;
          z-index:1;
        }

        /* Header */
        .ff-header{
          display:flex;
          flex-direction:column;
          align-items:center;
          gap:18px;
          padding:0 4vw 40px;
          text-align:center;
        }
        .ff-eyebrow-row{
          display:flex;
          align-items:center;
          justify-content:center;
          gap:24px;
          width:100%;
        }
        .ff-eyebrow-row .ff-line{
          flex:1;
          height:1px;
          background:var(--red);
        }
        .ff-eyebrow{
          margin:0;
          font-size:clamp(20px,2.6vw,32px);
          font-weight:700;
          letter-spacing:0.02em;
          text-transform:uppercase;
          white-space:nowrap;
        }
        .ff-eyebrow .accent{color:var(--red);}

        .ff-subrow{
          display:flex;
          align-items:center;
          justify-content:center;
          gap:16px;
          width:100%;
        }
        .ff-subrow .ff-line{
          flex:1;
          height:1.5px;
          background:var(--line);
          max-width:260px;
        }
        .ff-subrow .ff-dash{
          color:var(--red);
          font-size:14px;
        }
        .ff-club-name{
          margin:0;
          font-size:clamp(14px,1.4vw,17px);
          letter-spacing:0.02em;
          font-weight:500;
          color:var(--off);
          white-space:nowrap;
        }
        .ff-club-name .accent{color:var(--red); font-weight:700;}

        /* Main strip: left text + edge to edge auto-scrolling photos */
        .ff-strip-row{
          display:flex;
          align-items:stretch;
          width:100%;
          min-height:300px;
        }

        .ff-info-col{
          flex:0 0 clamp(230px,20vw,340px);
          display:flex;
          flex-direction:column;
          justify-content:center;
          padding-left: 6vw;
          padding-right: 3vw;
          gap:16px;
        }
        .ff-title{
          margin:0;
          line-height:0.98;
        }
        .ff-title span{
          display:block;
          font-weight:800;
          font-size:clamp(26px,3vw,38px);
          letter-spacing:0.01em;
          text-transform:uppercase;
        }
        .ff-title .word-friendship{
          color:var(--white);
          font-size:clamp(30px,4vw,50px);
          }

        .ff-title .word-fiesta{
          color:var(--red);
          font-size:clamp(30px,5vw,70px);
          }

        .ff-title-rule{
          width:34px;
          height:2px;
          background:var(--red);
        }

        .ff-view-link{
          display:inline-flex;
          align-items:center;
          gap:9px;
          font-size:12px;
          font-weight:600;
          letter-spacing:0.14em;
          text-transform:uppercase;
          color:var(--white);
          background:none;
          border:none;
          cursor:pointer;
          padding:0;
          font-family:inherit;
        }

        .ff-view-link svg{width:15px; height:15px; stroke:var(--red);}
        
        .ff-view-link:hover{color:var(--off);}

        .ff-photos{
          flex:1;
          min-width:0;
          overflow:hidden;
          position:relative;
          -webkit-mask-image:linear-gradient(to right, transparent 0, black 90px, black calc(100% - 90px), transparent 100%);
          mask-image:linear-gradient(to right, transparent 0, black 90px, black calc(100% - 90px), transparent 100%);
        }

        .ff-track{
          display:flex;
          align-items:stretch;
          gap:var(--ff-frame-gap);
          height:100%;
          width:max-content;
          animation:ffScroll 52s linear infinite;
        }
        
        .ff-photos:hover .ff-track{
          animation-play-state:paused;
        }

        @keyframes ffScroll{
          from{ transform:translateX(0); }
          to{ transform:translateX(calc(-1 * (var(--ff-frame-w) + var(--ff-frame-gap)) * ${photos.length})); }
        }

        .ff-frame{
          position:relative;
          flex:0 0 var(--ff-frame-w);
          width:var(--ff-frame-w);
          height:100%;
          overflow:hidden;
          background:
            linear-gradient(160deg, rgba(225,0,26,0.14), transparent 55%),
            repeating-linear-gradient(135deg, #141414 0px, #141414 3px, #0c0c0c 3px, #0c0c0c 24px);
        }
        
        .ff-frame img{
          width:100%;
          height:100%;
          object-fit:cover;
          object-position:center;
          display:block;
          transition:transform .6s ease;
        }
        
        .ff-frame.clickable{cursor:pointer;}
        
        .ff-frame.clickable:hover img{transform:scale(1.04);}
        
        .ff-frame.clickable:focus-visible{outline:2px solid var(--red); outline-offset:-2px;}

        .ff-placeholder{
          position:absolute;
          inset:0;
          display:flex;
          align-items:center;
          justify-content:center;
          flex-direction:column;
          gap:8px;
          color:#4a4a4a;
          font-size:10.5px;
          letter-spacing:0.1em;
          text-transform:uppercase;
          text-align:center;
          padding:0 16px;
        }

        /* Footer: caption left, dots center, caption right */
        .ff-footer{
          display:flex;
          align-items:center;
          padding:26px 3vw 0;
          padding-left: 6vw;
          justify-content:space-between;
          gap:16px;
          flex-wrap:wrap;
        }

        .ff-hint{
          display:inline-flex;
          align-items:center;
          gap:8px;
          font-size:9.5px;
          font-weight:600;
          letter-spacing:0.1em;
          text-transform:uppercase;
          color:var(--grey);
          white-space:nowrap;
        }
        
        .ff-hint svg{width:14px; height:14px; stroke:var(--red); flex-shrink:0;}

        .ff-dots{
          display:flex;
          align-items:center;
          gap:8px;
          margin:0 auto;
        }
        
        .ff-dot{
          width:7px;
          height:7px;
          border-radius:50%;
          background:#3a3a3a;
          border:none;
          padding:0;
          cursor:pointer;
          transition:all .25s ease;
        }
        
        .ff-dot.active{
          width:24px;
          border-radius:4px;
          background:var(--red);
        }

        /* Lightbox */
        .ff-lightbox-overlay{
          position:fixed;
          inset:0;
          background:rgba(6,6,6,0.96);
          z-index:999;
          display:flex;
          align-items:center;
          justify-content:center;
          padding:40px;
          animation:ffOverlayFade .25s ease;
        }
        
        @keyframes ffOverlayFade{ from{opacity:0;} to{opacity:1;} }

        .ff-lightbox-stage{
          max-width:min(90vw,1000px);
          max-height:88vh;
          display:flex;
          flex-direction:column;
          align-items:center;
        }
        
        .ff-lightbox-stage img{
          max-width:100%;
          max-height:76vh;
          object-fit:contain;
          border:1px solid var(--line);
          display:block;
        }
        
        .ff-lightbox-caption{
          display:flex;
          align-items:center;
          gap:14px;
          margin-top:18px;
          font-size:13px;
          color:var(--off);
          letter-spacing:0.03em;
        }
        
        .ff-lightbox-num{
          font-weight:800;
          font-size:16px;
          color:var(--red);
        }
        
        .ff-lightbox-count{
          margin-left:auto;
          color:var(--grey);
          font-size:12px;
          letter-spacing:0.08em;
          text-transform:uppercase;
        }
        
        .ff-lightbox-close{
          position:absolute;
          top:26px; right:26px;
          width:44px; height:44px;
          border-radius:50%;
          border:1px solid #333;
          background:transparent;
          color:var(--white);
          display:flex; align-items:center; justify-content:center;
          cursor:pointer;
          transition:border-color .25s ease, background .25s ease;
        }
        
        .ff-lightbox-close:hover{border-color:var(--red); background:rgba(225,0,26,0.08);}
        
        .ff-lightbox-close svg{width:18px;height:18px;}
        
        .ff-lightbox-nav{
          width:52px; height:52px;
          border-radius:50%;
          border:1px solid #333;
          background:transparent;
          color:var(--white);
          display:flex; align-items:center; justify-content:center;
          cursor:pointer;
          flex:none;
          transition:border-color .25s ease, background .25s ease;
        }
        
        .ff-lightbox-nav:hover{border-color:var(--red); background:rgba(225,0,26,0.08);}
        
        .ff-lightbox-nav svg{width:20px;height:20px;}
        
        .ff-lightbox-prev{margin-right:24px;}
        
        .ff-lightbox-next{margin-left:24px;}

        @media (max-width:900px){
          .ff-strip-row{flex-direction:column; min-height:0;}
          .ff-info-col{flex:none; padding:0 6vw 22px; align-items:flex-start;}
          .ff-photos{height:240px;}
          .ff-footer{justify-content:center; text-align:center;}
          .ff-dots{order:-1; margin:0 0 6px;}
        }
        
        @media (max-width:640px){
          .ff-section{ --ff-frame-w: 320px; padding:44px 0 34px; }
          .ff-photos{ height:200px; -webkit-mask-image:linear-gradient(to right, transparent 0, black 36px, black calc(100% - 36px), transparent 100%); mask-image:linear-gradient(to right, transparent 0, black 36px, black calc(100% - 36px), transparent 100%); }
          .ff-footer{padding:22px 6vw 0;}
          .ff-hint{font-size:10px;}
          .ff-lightbox-overlay{padding:20px;}
          .ff-lightbox-nav{width:40px; height:40px;}
          .ff-lightbox-prev{margin-right:10px;}
          .ff-lightbox-next{margin-left:10px;}
          .ff-lightbox-close{top:16px; right:16px; width:38px; height:38px;}
          .ff-lightbox-caption{flex-wrap:wrap; font-size:12px;}
        }

        @media (prefers-reduced-motion: reduce){
          .ff-track{animation:none;}
          .ff-frame img{transition:none;}
          .ff-dot{transition:none;}
        }
      `}</style>

      <canvas ref={canvasRef} className="ff-canvas" />

      <div className="ff-header">
        <div className="ff-eyebrow-row">
          <span className="ff-line" />
          <p className="ff-eyebrow">Our most recent <span className="accent">Event</span></p>
          <span className="ff-line" />
        </div>
        <div className="ff-subrow">
          <span className="ff-line" />
          
          <p className="ff-club-name"><span className="accent">TEDx</span>IITGuwahati</p>
          
          <span className="ff-line" />
        </div>
      </div>

      <div className="ff-strip-row">
        <div className="ff-info-col">
          <h1 className="ff-title">
            <span className="word-friendship">Friendship</span>
            <span className="word-fiesta">Fiesta</span>
          </h1>
          <span className="ff-title-rule" />
          <button className="ff-view-link" type="button" onClick={() => openAt(0)}>
            View glimpses
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <div className="ff-photos">
          <div className="ff-track">
            {trackPhotos.map((photo, i) => (
              <Frame
                photo={photo}
                key={i}
                num={String((i % photos.length) + 1).padStart(2, "0")}
                onOpen={() => openAt(i % photos.length)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="ff-footer">
        <span className="ff-hint">Scroll pauses on hover</span>

        <div className="ff-dots">
          {Array.from({ length: dotCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={i === activeDot ? "ff-dot active" : "ff-dot"}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setActiveDot(i)}
            />
          ))}
        </div>

        <span className="ff-hint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" />
          </svg>
          Click a frame to view it full screen
        </span>
      </div>

      <Lightbox photos={photos} activeIndex={activeIndex} onClose={close} onPrev={goPrev} onNext={goNext} />
    </section>
  );
}